// client/src/coms/settings/sections/AdvancedSettings.js
// Personal Settings → Advanced: where each kind of setting is stored (technical names live here, not in the
// everyday settings), device diagnostics for support, and resetting this device's local settings.
import React, { useMemo } from 'react';
import { Row, Section } from '../../personalisation/settingsControls';
import { useConfirm, notify } from '../../organisation/orgShared';
import { usePersonalisation } from '../../personalisation/PersonalisationProvider';
import { canSetSinkId } from '../useDeviceSettings';

// Settings kept in this browser only. Clearing them never touches account settings, messages or files.
export const DEVICE_SETTING_KEYS = [
  ['Microphone, speakers, camera', ['coms_audio_input_device_id', 'coms_audio_output_device_id', 'coms_voice_output_device_id', 'coms_video_input_device_id']],
  ['Microphone sensitivity', ['coms_noise_gate_threshold_db']],
  ['Mute hotkeys', ['vox:muteMicShortcut', 'vox:muteSpeakerShortcut', 'vox:muteBothShortcut']],
  ['Server navigation (dropdown or sidebar)', ['vox:serverNavMode']],
  ['Sticker suggestions while typing', ['vox:stickerSuggestions']],
  ['Soundboard volume and favourites', ['vox:soundboardPrefs', 'vox:soundboardTab']],
  ['Desktop notifications turned off here', ['vox:push:disabled']],
];

const ACCOUNT_SETTINGS = [
  'Theme, accent, custom themes, text size, interface scale, reduce motion, focus outlines',
  'Layout preset, panel arrangement and sizes, tabs and split view',
  'Per-server preferences (This Server)',
  'Speaking animation, avatar, appear offline, Do Not Disturb, Steam, cloud storage accounts',
  'Sticker packs and soundboard sounds',
];

export default function AdvancedSettings({ dev }) {
  const pz = usePersonalisation();
  const [confirm, confirmEl] = useConfirm();

  const diag = useMemo(
    () => [
      ['Microphones / outputs / cameras seen', `${dev.counts.audioinput} / ${dev.counts.audiooutput} / ${dev.counts.videoinput}`],
      ['Choose output device', canSetSinkId() ? 'Supported' : 'Not supported in this browser'],
      ['Notifications permission', typeof Notification !== 'undefined' ? Notification.permission : 'Not supported'],
      ['Reduced motion', pz.reduceMotion ? `On${pz.systemReducedMotion ? ' (device setting)' : ''}` : 'Off'],
      ['Theme in use', pz.resolvedTheme ? pz.resolvedTheme.name || pz.resolvedTheme.id : '—'],
      ['Screen', typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight} at ${window.devicePixelRatio || 1}×` : '—'],
      ['Browser', typeof navigator !== 'undefined' ? navigator.userAgent : '—'],
    ],
    [dev.counts, pz.reduceMotion, pz.systemReducedMotion, pz.resolvedTheme]
  );

  const resetDevice = async () => {
    const ok = await confirm({
      title: 'Reset this device’s settings?',
      body: 'Your microphone, speaker and camera choices, microphone sensitivity, mute hotkeys, server navigation and soundboard preferences go back to their defaults in this browser. Account settings (theme, layout, animation) and your messages and files are not affected.',
      confirmLabel: 'Reset this device',
      danger: true,
    });
    if (!ok) return;
    for (const [, keys] of DEVICE_SETTING_KEYS) {
      for (const k of keys) {
        try {
          window.localStorage.removeItem(k);
        } catch {}
      }
    }
    notify('This device’s settings were reset. Reloading…', { tone: 'success' });
    setTimeout(() => window.location.reload(), 900);
  };

  return (
    <Section id="vx-cat-advanced" title="Advanced" description="For troubleshooting. Nothing here is needed for everyday use.">
      <div id="vx-setting-storage-map" data-setting="storage-map" className="vx-set-block">
        <h4 className="vx-set-subtitle">Where your settings are stored</h4>
        <p className="vx-set-hint">Account settings follow you to every device and survive signing out. Device settings stay in this browser, because devices and keys differ from computer to computer.</p>
        <div className="vx-store-grid">
          <div>
            <div className="vx-store-head">
              Your account <span className="vx-scope is-account">Account</span>
            </div>
            <ul className="vx-store-list">
              {ACCOUNT_SETTINGS.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="vx-store-head">
              This browser <span className="vx-scope is-device">This device</span>
            </div>
            <ul className="vx-store-list">
              {DEVICE_SETTING_KEYS.map(([label, keys]) => (
                <li key={label}>
                  {label} <code>{keys.join(', ')}</code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div id="vx-setting-diagnostics" data-setting="diagnostics" className="vx-set-block">
        <h4 className="vx-set-subtitle">Device diagnostics</h4>
        <table className="vx-diag">
          <tbody>
            {diag.map(([k, v]) => (
              <tr key={k}>
                <th scope="row">{k}</th>
                <td>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Row label="Reset this device’s settings" hint="Puts this browser’s device settings back to their defaults. Account settings are not changed." settingId="reset-device" scope="device">
        <button type="button" className="vx-btn small danger" onClick={resetDevice} data-testid="settings-reset-device">
          Reset this device…
        </button>
      </Row>
      {confirmEl}
    </Section>
  );
}
